import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import DataTable from "../../components/DataTable";
import Footer from "../../components/Footer";
import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Swal from "sweetalert2";
import { api } from "../../axios/api";
import Modal from "react-bootstrap/Modal";
import { Plus, Edit, Trash2, RefreshCw, Search, Upload } from "lucide-react";

const Clientes = () => {
    const user = useSelector((state) => state.user.user);
    const empresaId = user?.empresa_id;
    const userId = user?.id;
    const importInputRef = useRef(null);

    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    
    // Estado estruturado estritamente com os campos descritos no modelo Cliente do Sequelize
    const [clienteData, setClienteData] = useState({
        nome: "",
        nif: "",
        telefone: "",
        email: "",
        endereco: "",
        empresa_id: empresaId,
        user_id: userId,
    });
    
    const [data, setData] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [searchingNif, setSearchingNif] = useState(false); 

    useEffect(() => {
        if (empresaId) fetchClientes();
    }, [empresaId]);

    const fetchClientes = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/app/clientes/empresa/${empresaId}`);
            setData(response.data);
        } catch (error) {
            console.error("Error fetching customers:", error);
        } finally {
            setLoading(false);
        }
    };

    // Autocomplete via API mapeando apenas os campos existentes na tabela
    const handleSearchByNif = async () => {
        if (!clienteData.nif || clienteData.nif.trim() === "") {
            return Swal.fire("Informação", "Por favor, digite um NIF para pesquisar.", "info");
        }

        setSearchingNif(true);
        try {
            const response = await api.get(`/app/clientes/nif/${clienteData.nif.trim()}`);
            const infoAgt = response.data?.ObterContribuinte.contribuinte || response.data;
            
            if (infoAgt) {
                setClienteData({
                    ...clienteData,
                    nome: infoAgt.nome || infoAgt.razonSocial || infoAgt.razao_social || "",
                    endereco: infoAgt.domicilioFiscal || infoAgt.endereco || "",
                    telefone: infoAgt.telefone || clienteData.telefone || "",
                    email: infoAgt.email || clienteData.email || ""
                });
                
                Swal.fire({
                    title: "Sucesso",
                    text: "Dados sincronizados com a AGT!",
                    icon: "success",
                    timer: 1800,
                    showConfirmButton: false
                });
            } else {
                Swal.fire("Informação", "Nenhum cliente encontrado com este NIF na AGT.", "info");
            }
        } catch (error) {
            console.error("Erro ao buscar NIF na AGT:", error);
            Swal.fire("Informação", "Nenhum cliente encontrado com este NIF na AGT.", "info");
        } finally {
            setSearchingNif(false);
        }
    };

    const handleSelectRow = (e) => {
        const tr = e.target.closest("tr");
        if (tr && tr.parentElement.tagName === "TBODY") {
            const firstCell = tr.cells[0];
            if (firstCell) {
                const id = parseInt(firstCell.innerText);
                if (!isNaN(id)) {
                    setSelectedId(id);
                    tr.parentElement.querySelectorAll("tr").forEach(row => row.classList.remove("win10-row-selected"));
                    tr.classList.add("win10-row-selected");
                }
            }
        }
    };

    const handleOpenAddModal = () => {
        setIsEditing(false);
        setClienteData({ 
            nome: "",
            nif: "",
            telefone: "",
            email: "",
            endereco: "",
            empresa_id: empresaId, 
            user_id: userId 
        });
        setShowModal(true);
    };

    const handleOpenEditModal = () => {
        if (!selectedId) {
            return Swal.fire({
                title: "Informação",
                text: "Selecione um cliente na tabela primeiro.",
                icon: "info",
                confirmButtonColor: "#0078d4"
            });
        }
        const item = data.find(c => c.id === selectedId);
        if (item) {
            setIsEditing(true);
            setClienteData(item);
            setShowModal(true);
        }
    };

    const handleDelete = async () => {
        if (!selectedId) return;

        Swal.fire({
            title: "Confirmar Acção",
            text: "Deseja realmente eliminar este cliente?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d13438",
            cancelButtonColor: "#8a8886",
            confirmButtonText: "Sim, eliminar",
            cancelButtonText: "Cancelar"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await api.delete(`/app/clientes/${selectedId}`);
                    setData(data.filter((c) => c.id !== selectedId));
                    setSelectedId(null);
                    Swal.fire("Sucesso", "Cliente removido com sucesso.", "success");
                } catch (error) {
                    Swal.fire("Erro", "Falha ao processar o pedido.", "error");
                }
            }
        });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await api.put(`/app/clientes/${clienteData.id}`, clienteData);
            } else {
                await api.post("/app/clientes/", clienteData);
            }
            fetchClientes();
            setShowModal(false);
            setSelectedId(null);
            Swal.fire("Sucesso", "Dados guardados com sucesso.", "success");
        } catch (error) {
            Swal.fire("Erro", "Falha ao processar o pedido.", "error");
        }
    };

    const handleImportClientes = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        formData.append("file", file);
        formData.append("user_id", userId);

        try {
            const response = await api.post(`/app/clientes/import/${empresaId}/`, formData);
            await fetchClientes();
            Swal.fire("Importado", response.data.message || "Clientes importados com sucesso.", "success");
        } catch (error) {
            Swal.fire("Erro", error.response?.data?.error || "Falha ao importar clientes.", "error");
        } finally {
            event.target.value = "";
        }
    };

    return (
        <div id="layout-wrapper">
            <Navbar />
            <Sidebar />

            <style>{`
                .win10-card { background: #fff; border: 1px solid #e1e1e1; border-radius: 0px; }
                .win10-header { border-bottom: 1px solid #eee; padding: 12px 20px; background: #fff; }
                .win10-row-selected { background-color: #0078d4 !important; color: #fff !important; }
                .win10-row-selected td { color: #fff !important; }
                .win10-btn { border-radius: 0px; font-size: 12px; padding: 6px 14px; border: 1px solid transparent; transition: 0.1s; }
                .win10-btn-primary { background: #0078d4; color: #fff; }
                .win10-btn-primary:disabled { background: #a6d1f5; }
                .win10-btn-outline { background: #fff; border: 1px solid #8a8886; color: #323130; }
                .win10-btn-outline:hover { background: #f3f2f1; }
                .fs-14 { font-size: 14px; }
            `}</style>

            <main className="app-wrapper animate__animated animate__fadeIn">
                <div className="container-fluid mt-3">
                    <div className="win10-card shadow-sm">
                        <div className="win10-header d-flex justify-content-between align-items-center flex-wrap">
                            <span className="text-muted small fw-bold text-uppercase">Explorador de Clientes</span>
                            
                            <div className="d-flex gap-1">
                                <input ref={importInputRef} type="file" accept=".csv,.json,.sql" hidden onChange={handleImportClientes} />
                                <button className="win10-btn win10-btn-primary d-flex align-items-center gap-2" onClick={handleOpenAddModal}>
                                    <Plus size={14}/> Novo
                                </button>
                                <button className="win10-btn win10-btn-outline d-flex align-items-center gap-2" onClick={handleOpenEditModal}>
                                    <Edit size={14}/> Editar
                                </button>
                                <button className="win10-btn win10-btn-outline d-flex align-items-center gap-2" onClick={() => importInputRef.current?.click()}>
                                    <Upload size={14}/> Importar
                                </button>
                                <button className="win10-btn win10-btn-outline text-danger d-flex align-items-center gap-2" onClick={handleDelete}>
                                    <Trash2 size={14}/> Eliminar
                                </button>
                            </div>
                        </div>

                        <div className="card-body p-0" onClick={handleSelectRow}>
                            <div className="p-3 table-responsive w-100" >
                                <DataTable 
                                    columns={[
                                        { title: "ID", data: "id" },
                                        { title: "NOME", data: "nome" },
                                        { title: "NIF", data: "nif" },
                                        { title: "TELEFONE", data: "telefone" },
                                        { title: "EMAIL", data: "email", render: d => d || "---" },
                                    ]} 
                                    data={data} 
                                    loading={loading}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" backdrop="static" contentClassName="rounded-0 border-primary">
                <form onSubmit={handleSave}>
                    <Modal.Header closeButton className="py-2">
                        <Modal.Title className="fs-14 fw-bold text-uppercase">
                            {isEditing ? "Propriedades do Cliente" : "Novo Registo de Cliente"}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="row g-2 p-4">
                        
                        <div className="col-md-4">
                            <label className="small fw-bold text-muted text-uppercase">NIF</label>
                            <div className="input-group">
                                <input 
                                    type="text" 
                                    className="form-control form-control-sm rounded-0 border-secondary" 
                                    value={clienteData.nif || ""} 
                                    onChange={e => setClienteData({...clienteData, nif: e.target.value})} 
                                />
                                <button 
                                    type="button" 
                                    className="btn btn-primary btn-sm rounded-0 d-flex align-items-center justify-content-center"
                                    onClick={handleSearchByNif}
                                    disabled={searchingNif}
                                    style={{ background: "#0078d4", borderColor: "#0078d4" }}
                                >
                                    {searchingNif ? (
                                        <RefreshCw size={14} className="animate-spin" />
                                    ) : (
                                        <Search size={14}/>
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className="col-md-8">
                            <label className="small fw-bold text-muted text-uppercase">Nome Completo / Razão Social</label>
                            <input 
                                type="text" 
                                className="form-control form-control-sm rounded-0 border-secondary" 
                                required 
                                value={clienteData.nome || ""} 
                                onChange={e => setClienteData({...clienteData, nome: e.target.value})} 
                            />
                        </div>

                        <div className="col-md-6">
                            <label className="small fw-bold text-muted text-uppercase">Telefone Principal</label>
                            <input 
                                type="text" 
                                className="form-control form-control-sm rounded-0 border-secondary" 
                                value={clienteData.telefone || ""} 
                                onChange={e => setClienteData({...clienteData, telefone: e.target.value})} 
                            />
                        </div>
                        
                        <div className="col-md-6">
                            <label className="small fw-bold text-muted text-uppercase">Email</label>
                            <input 
                                type="email" 
                                className="form-control form-control-sm rounded-0 border-secondary" 
                                value={clienteData.email || ""} 
                                onChange={e => setClienteData({...clienteData, email: e.target.value})} 
                            />
                        </div>

                        <div className="col-12">
                            <label className="small fw-bold text-muted text-uppercase">Endereço / Sede</label>
                            <textarea 
                                rows="2" 
                                className="form-control form-control-sm rounded-0 border-secondary" 
                                value={clienteData.endereco || ""} 
                                onChange={e => setClienteData({...clienteData, endereco: e.target.value})}
                            ></textarea>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 bg-light py-2">
                        <button type="button" className="win10-btn win10-btn-outline px-4" onClick={() => setShowModal(false)}>Cancelar</button>
                        <button type="submit" className="win10-btn win10-btn-primary px-4" disabled={searchingNif}>Gravar Dados</button>
                    </Modal.Footer>
                </form>
            </Modal>

            <Footer />
        </div>
    );
};

export default Clientes;
